import React, { FC, useCallback, useState } from "react";
import { Dialog, ButtonProps, Input } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface FieldRoutesConnectionProps extends IntegrationConnectionProps {
    integration: "fieldroutes";
    subdomain: string;
    authenticationKey: string;
    authenticationToken: string;
    sourceID?: string;
    defaultAppointmentType?: string;
}

export interface FieldRoutesIntegrationCardProps extends IntegrationCardProp {
    subdomain?: string;
    authenticationKey?: string;
    authenticationToken?: string;
    sourceID?: string;
    defaultAppointmentType?: string;
    /**
     * Connect FieldRoutes
     */
    onConnect?(props: FieldRoutesConnectionProps): void;
}

export const FieldRoutesIntegrationCard: FC<FieldRoutesIntegrationCardProps> = (props) => {
    const { checked, disabled, isLinked, isActive, onDisconnect, onConnect } = props;

    const [subdomain, setSubdomain] = useState<string>(props.subdomain || "");
    const [authenticationKey, setAuthenticationKey] = useState<string>(props.authenticationKey || "");
    const [authenticationToken, setAuthenticationToken] = useState<string>(props.authenticationToken || "");
    const [sourceID, setSourceID] = useState<string>(props.sourceID || "");
    const [defaultAppointmentType, setDefaultAppointmentType] = useState<string>(props.defaultAppointmentType || "");
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const handleFieldRoutesClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            // pop up modal
            setShowDialog(true);
        }
    };

    const handleConnectFieldRoutes = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "fieldroutes",
                subdomain: subdomain.trim(),
                authenticationKey: authenticationKey.trim(),
                authenticationToken: authenticationToken.trim(),
                sourceID: sourceID.trim() || undefined,
                defaultAppointmentType: defaultAppointmentType.trim() || undefined,
            });
        }
        setShowDialog(false);
    }, [onConnect, subdomain, authenticationKey, authenticationToken, sourceID, defaultAppointmentType]);

    const handleDisconnectFieldRoutes = useCallback(() => {
        if (typeof onDisconnect === "function") {
            onDisconnect();
            setShowDialog(false);
        }
    }, [onDisconnect]);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: () => {
                setShowDialog(false);
            },
        },
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            disabled: !subdomain.trim() || !authenticationKey.trim() || !authenticationToken.trim(),
            onClick: handleConnectFieldRoutes,
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/field-routes/fieldroutes-logo.svg"
                text="FieldRoutes"
                badge="TEST"
                href={() => "/integrations/fieldroutes"}
                onClick={handleFieldRoutesClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={typeof onDisconnect === "function" ? handleDisconnectFieldRoutes : undefined}
            />
            <Dialog
                active={showDialog}
                actions={actions}
                title="FieldRoutes Setup"
                onClose={() => {
                    setShowDialog(false);
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    <div>
                        <p>Connect your FieldRoutes account to automatically sync leads and customer data.</p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Subdomain"
                            value={subdomain}
                            onChange={(value) => setSubdomain(String(value))}
                            placeholder="e.g. acmepest"
                        />
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Authentication Key"
                            value={authenticationKey}
                            onChange={(value) => setAuthenticationKey(String(value))}
                        />
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Authentication Token"
                            value={authenticationToken}
                            type="password"
                            onChange={(value) => setAuthenticationToken(String(value))}
                        />
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Source ID (Optional)"
                            value={sourceID}
                            onChange={(value) => setSourceID(String(value))}
                        />
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Default Appointment Type (Optional)"
                            value={defaultAppointmentType}
                            onChange={(value) => setDefaultAppointmentType(String(value))}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};
